// 9fbef606107a605d69c0edbcd8029e5d
import { isCanada } from '@tcp/core/src/utils/utils';
import { extractAttributeValue } from '@tcp/core/src/services/abstractors/productListing/productParser';
import {
  getDynamicLabelValues,
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { globalCommonBrowseLabels } from '@tcp/core/src/utils/labels/globalCommonBrowseLabels.labels';
import { globalSortingLabels } from '@tcp/core/src/utils/labels/globalSorting.labels';
import { PLPLabels } from '@tcp/core/src/utils/labels/browse/PLP.labels';
import { triggerOneClickATC } from './plpTileCommonHelpers';

const setQuickViewAlternateBrand = ({ acrossBrandApiCalled, resetSlpLoadingState, brand }) => {
  if (acrossBrandApiCalled && resetSlpLoadingState) {
    resetSlpLoadingState({ alternateBrandName: brand });
  }
};

export const handleClickFunc = ({
  isBundle,
  handleViewBundleClick,
  setProductDataForPdp,
  tileData,
  isOneClickATCEnabled,
  isBopisFilterOn,
  topBadge,
  brand,
  imageUrl,
  addToBagEcom,
  saveDataForQv,
  onQuickViewOpenClick,
  productPartNo,
  acrossBrandApiCalled,
  resetSlpLoadingState,
  alternateBrandsShown,
}) => {
  if (isBundle) {
    handleViewBundleClick();
    setProductDataForPdp();
  } else {
    const hasTriggeredOneClickATC = triggerOneClickATC({
      tileData,
      isOneClickATCEnabled,
      isBopisFilterOn,
      topBadge,
      brand,
      imageUrl,
      addToBagEcom,
      isSLPTile: true,
    });
    if (!hasTriggeredOneClickATC) {
      setQuickViewAlternateBrand({
        acrossBrandApiCalled,
        resetSlpLoadingState,
        brand: alternateBrandsShown,
      });
      saveDataForQv();
      onQuickViewOpenClick({
        colorProductId: productPartNo,
        fromPage: 'SLP',
      });
    }
  }
};

export const getTCPPrimaryCategory = (tileData) => {
  return !isCanada()
    ? extractAttributeValue(tileData, 'TCPPrimaryCategoryUSStore')
    : extractAttributeValue(tileData, 'TCPPrimaryCategoryCanadaStore');
};

// Utility function to handle image URL on mouse enter
export const handleMouseEnterImage = (
  altImage,
  productPartNo,
  isAlternateImageOnHoverEnabled,
  assetPath,
  stylePartNo,
  setNewImageUrl
) => {
  if (altImage && productPartNo && isAlternateImageOnHoverEnabled) {
    const altImageParsed = JSON.parse(altImage);
    const productImage = altImageParsed[productPartNo]?.split(',')[0];

    if (productImage) {
      const tempImageUrl = `${assetPath}/${stylePartNo}/${productImage}`;
      setNewImageUrl(tempImageUrl);
    }
  }
};

// Utility function to handle image URL on mouse leave
export const handleMouseLeaveImage = ({
  newImageUrl,
  imageUrl,
  isAlternateImageOnHoverEnabled,
  setNewImageUrl,
  isHoverToPreviewColorSwatchesEnabled,
}) => {
  if (
    newImageUrl !== imageUrl &&
    (isAlternateImageOnHoverEnabled || isHoverToPreviewColorSwatchesEnabled)
  ) {
    setNewImageUrl(imageUrl);
  }
};

// Utility function to determine the mounted tile redesign variation
export const getMountedTileRedesignVariation = (hasMounted, isTileRedesignAB) => {
  return hasMounted && isTileRedesignAB === 'variationB' ? 'variationB' : 'variationA';
};

// Utility function to calculate minimum prices
export const calculateMinPrices = (TCPCustomListPrice, minLP, TCPCustomSalePrice, minOP) => {
  return {
    minListPrice: TCPCustomListPrice || minLP,
    minOfferPrice: TCPCustomSalePrice || minOP,
  };
};

// Utility function to determine badge labels
export const getBadgeLabels = (isEnabled, attribute, label) => {
  return isEnabled && attribute && label ? label : '';
};

// Utility function to determine swatches based on site ID
export const getSwatches = (siteId, TCPSwatchesUSStore, TCPSwatchesCanadaStore) => {
  return siteId === 'us' ? TCPSwatchesUSStore : TCPSwatchesCanadaStore;
};

export const getAlternateBrand = ({ acrossBrandApiCalled, brand, primaryBrand }) => {
  return acrossBrandApiCalled && brand && brand?.toLowerCase() !== primaryBrand.toLowerCase()
    ? brand?.toLowerCase()
    : '';
};

export const getRelatedSwatches = (relatedSwatches) => {
  return (relatedSwatches && relatedSwatches.split(',')) || [];
};

export const getInfluencersPickToDisplay = ({ isInfluencerPicksEnabled, influencerPicks }) => {
  return isInfluencerPicksEnabled && influencerPicks && influencerPicks[0];
};

export const getAddToBagButtonText = ({
  isBundle,
  submitButtonText,
  pickupInStore,
  primaryBrand,
  shopTheLook,
  shopCollection,
}) => {
  if (isBundle && submitButtonText !== pickupInStore) {
    return primaryBrand === 'GYM' ? shopTheLook : shopCollection;
  }
  return submitButtonText;
};

export const getTileHoverProps = ({
  isMountedInteractiveColorSwatchBandEnabled,
  handleMouseEnter,
  handleMouseLeave,
}) => {
  return isMountedInteractiveColorSwatchBandEnabled
    ? {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      }
    : {};
};

export const getSLPTileLabels = () => {
  const globalBrowseLabels = getLabelFromCopyOrFromTranslation(
    'global.CommonBrowseLabels',
    globalCommonBrowseLabels
  );
  const bestSellerBadgeLabel = renderLabel(globalBrowseLabels.lbl_best_seller, 'lbl_best_seller');
  const sortingLabels = getLabelFromCopyOrFromTranslation('global.Sorting', globalSortingLabels);
  const topRatedBadgeLabel = renderLabel(
    sortingLabels.lbl_TCPBazaarVoiceRating,
    'lbl_TCPBazaarVoiceRating'
  );
  const mostFavoritedBadgeLabel = renderLabel(
    sortingLabels.lbl_favoritedcount,
    'lbl_favoritedcount'
  );
  const plpTileLabels = getLabelFromCopyOrFromTranslation('PLP.plpTiles', PLPLabels);
  const pickupInStore = renderLabel(
    plpTileLabels.lbl_plp_pickup_in_store,
    'lbl_plp_pickup_in_store'
  );
  const shopTheLook = renderLabel(
    plpTileLabels.lbl_plpTiles_shop_the_look,
    'lbl_plpTiles_shop_the_look'
  );
  const shopCollection = renderLabel(
    plpTileLabels.lbl_plpTiles_shop_collection,
    'lbl_plpTiles_shop_collection'
  );
  const pointsAndRewardsText = getDynamicLabelValues(
    'dynamic.browse_d.lbl_brierley_promo_text_generic'
  );

  return {
    bestSellerBadgeLabel,
    topRatedBadgeLabel,
    mostFavoritedBadgeLabel,
    pickupInStore,
    shopTheLook,
    shopCollection,
    pointsAndRewardsText,
  };
};

export const getInteractiveColorSwatchesClass = (isMountedInteractiveColorSwatchBandEnabled) => {
  return isMountedInteractiveColorSwatchBandEnabled
    ? ' interactive-color-swatches-image-container'
    : '';
};

export const getIsMountedHoverToRevealATBButtonEnabled = ({
  isHoverToRevealATBButtonEnabled,
  hasMounted,
  isDesktop,
}) => {
  let isMountedHoverToRevealATBButtonEnabled = false;
  if (hasMounted && isDesktop && isHoverToRevealATBButtonEnabled) {
    isMountedHoverToRevealATBButtonEnabled = true;
  }
  return isMountedHoverToRevealATBButtonEnabled;
};
